import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Plus } from 'lucide-react';
import { ProjectEpic, ProjectFeature, ProjectStory } from '../../types/content';
import Input from '../UI/Input';

const newKey = () => (crypto.randomUUID ? crypto.randomUUID() : `k-${Date.now()}-${Math.random()}`);

type OpenMap = Record<string, boolean>;

const isOpen = (open: OpenMap, key: string) => open[key] !== false;

interface ProjectRequirementsTreeProps {
  projectTitle: string;
  epics: ProjectEpic[];
  features: ProjectFeature[];
  stories: ProjectStory[];
  setEpics: React.Dispatch<React.SetStateAction<ProjectEpic[]>>;
  setFeatures: React.Dispatch<React.SetStateAction<ProjectFeature[]>>;
  setStories: React.Dispatch<React.SetStateAction<ProjectStory[]>>;
}

const ProjectRequirementsTree: React.FC<ProjectRequirementsTreeProps> = ({
  projectTitle,
  epics,
  features,
  stories,
  setEpics,
  setFeatures,
  setStories,
}) => {
  const [open, setOpen] = useState<OpenMap>({ project: true });

  const toggle = (key: string) => {
    setOpen((current) => ({ ...current, [key]: !isOpen(current, key) }));
  };

  const expand = (...keys: string[]) => {
    setOpen((current) => {
      const next = { ...current };
      keys.filter(Boolean).forEach((key) => { next[key] = true; });
      return next;
    });
  };

  const addEpic = () => {
    const clientKey = newKey();
    setEpics((current) => [...current, { title: '', description: '', displayOrder: current.length + 1, clientKey }]);
    expand('project', clientKey);
  };

  const addFeature = (epicClientKey: string) => {
    const clientKey = newKey();
    setFeatures((current) => [...current, {
      title: '',
      description: '',
      displayOrder: current.length + 1,
      clientKey,
      epicClientKey,
    }]);
    expand('project', epicClientKey, clientKey);
  };

  const addStory = (featureClientKey: string, epicClientKey?: string | null) => {
    const clientKey = newKey();
    setStories((current) => [...current, {
      title: '',
      description: '',
      acceptanceCriteria: '',
      displayOrder: current.length + 1,
      featureClientKey,
      clientKey,
    }]);
    expand('project', epicClientKey || '', featureClientKey, clientKey);
  };

  const removeEpic = (epicKey: string) => {
    const featureKeys = new Set(
      features.filter((item) => item.epicClientKey === epicKey).map((item) => item.clientKey).filter(Boolean) as string[]
    );
    setEpics((current) => current.filter((item) => item.clientKey !== epicKey));
    setFeatures((current) => current.filter((item) => item.epicClientKey !== epicKey));
    setStories((current) => current.filter((item) => !item.featureClientKey || !featureKeys.has(item.featureClientKey)));
  };

  const removeFeature = (featureKey: string) => {
    setFeatures((current) => current.filter((item) => item.clientKey !== featureKey));
    setStories((current) => current.filter((item) => item.featureClientKey !== featureKey));
  };

  const updateEpic = (clientKey: string, patch: Partial<ProjectEpic>) => {
    setEpics((current) => current.map((item) => item.clientKey === clientKey ? { ...item, ...patch } : item));
  };

  const updateFeature = (clientKey: string, patch: Partial<ProjectFeature>) => {
    setFeatures((current) => current.map((item) => item.clientKey === clientKey ? { ...item, ...patch } : item));
  };

  const updateStory = (clientKey: string, patch: Partial<ProjectStory>) => {
    setStories((current) => current.map((item) => item.clientKey === clientKey ? { ...item, ...patch } : item));
  };

  const featuresForEpic = (epicKey: string) => features.filter((item) => item.epicClientKey === epicKey);
  const storiesForFeature = (featureKey: string) => stories.filter((item) => item.featureClientKey === featureKey);
  const knownEpicKeys = new Set(epics.map((item) => item.clientKey).filter(Boolean));
  const knownFeatureKeys = new Set(features.map((item) => item.clientKey).filter(Boolean));
  const unassignedFeatures = features.filter((item) => !item.epicClientKey || !knownEpicKeys.has(item.epicClientKey));
  const unassignedStories = stories.filter((item) => !item.featureClientKey || !knownFeatureKeys.has(item.featureClientKey));

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      <TreeRow
        open={isOpen(open, 'project')}
        onToggle={() => toggle('project')}
        label={projectTitle.trim() || 'Project'}
        kind="Project"
        onAdd={addEpic}
        addLabel="Add epic"
      />
      {isOpen(open, 'project') && (
        <div className="border-t border-gray-100 bg-gray-50/60 px-3 py-3 space-y-2">
          {epics.length === 0 && unassignedFeatures.length === 0 && unassignedStories.length === 0 && (
            <p className="text-sm text-gray-600 px-1">
              Use + beside the project to add epics, + beside an epic to add features, and + beside a feature to add stories.
            </p>
          )}
          {epics.map((epic, epicIndex) => {
            const epicKey = epic.clientKey || `epic-${epicIndex}`;
            const epicFeatures = featuresForEpic(epicKey);
            return (
              <div key={epicKey} className="rounded-lg border border-gray-200 bg-white">
                <TreeRow
                  open={isOpen(open, epicKey)}
                  onToggle={() => toggle(epicKey)}
                  label={epic.title.trim() || 'New epic'}
                  kind="Epic"
                  onAdd={() => addFeature(epicKey)}
                  addLabel="Add feature"
                  onRemove={() => removeEpic(epicKey)}
                />
                {isOpen(open, epicKey) && (
                  <div className="border-t border-gray-100 px-3 py-3 space-y-3 ml-3">
                    <Input
                      id={`epic-title-${epicKey}`}
                      label="Epic title"
                      value={epic.title}
                      onChange={(e) => updateEpic(epicKey, { title: e.target.value })}
                    />
                    <label className="block text-sm font-medium text-gray-700">
                      Epic description
                      <textarea
                        className="mt-1 w-full rounded-md border-gray-300"
                        rows={3}
                        value={epic.description || ''}
                        onChange={(e) => updateEpic(epicKey, { description: e.target.value })}
                      />
                    </label>
                    {epicFeatures.map((feature, featureIndex) => {
                      const featureKey = feature.clientKey || `f-${epicKey}-${featureIndex}`;
                      const featureStories = storiesForFeature(featureKey);
                      return (
                        <div key={featureKey} className="rounded-lg border border-gray-200">
                          <TreeRow
                            open={isOpen(open, featureKey)}
                            onToggle={() => toggle(featureKey)}
                            label={feature.title.trim() || 'New feature'}
                            kind="Feature"
                            onAdd={() => addStory(featureKey, epicKey)}
                            addLabel="Add story"
                            onRemove={() => removeFeature(featureKey)}
                          />
                          {isOpen(open, featureKey) && (
                            <div className="border-t border-gray-100 px-3 py-3 space-y-3 ml-3">
                              <Input
                                id={`feature-title-${featureKey}`}
                                label="Feature title"
                                value={feature.title}
                                onChange={(e) => updateFeature(featureKey, { title: e.target.value })}
                              />
                              <label className="block text-sm font-medium text-gray-700">
                                Description
                                <textarea
                                  className="mt-1 w-full rounded-md border-gray-300"
                                  rows={3}
                                  value={feature.description || ''}
                                  onChange={(e) => updateFeature(featureKey, { description: e.target.value })}
                                />
                              </label>
                              {featureStories.map((story, storyIndex) => {
                                const storyKey = story.clientKey || `s-${featureKey}-${storyIndex}`;
                                return (
                                  <StoryNode
                                    key={storyKey}
                                    storyKey={storyKey}
                                    story={story}
                                    open={isOpen(open, storyKey)}
                                    onToggle={() => toggle(storyKey)}
                                    onChange={(patch) => updateStory(storyKey, patch)}
                                    onRemove={() => setStories((current) => current.filter((item) => item.clientKey !== storyKey))}
                                  />
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          {unassignedFeatures.map((feature, featureIndex) => {
            const featureKey = feature.clientKey || `unassigned-f-${featureIndex}`;
            const featureStories = storiesForFeature(featureKey);
            return (
              <div key={featureKey} className="rounded-lg border border-amber-200 bg-amber-50/50">
                <TreeRow
                  open={isOpen(open, featureKey)}
                  onToggle={() => toggle(featureKey)}
                  label={`${feature.title.trim() || 'New feature'} (unassigned)`}
                  kind="Feature"
                  onAdd={() => addStory(featureKey)}
                  addLabel="Add story"
                  onRemove={() => removeFeature(featureKey)}
                />
                {isOpen(open, featureKey) && (
                  <div className="border-t border-amber-100 px-3 py-3 space-y-3 ml-3">
                    <Input
                      id={`feature-title-${featureKey}`}
                      label="Feature title"
                      value={feature.title}
                      onChange={(e) => updateFeature(featureKey, { title: e.target.value })}
                    />
                    {featureStories.map((story, storyIndex) => {
                      const storyKey = story.clientKey || `s-${featureKey}-${storyIndex}`;
                      return (
                        <StoryNode
                          key={storyKey}
                          storyKey={storyKey}
                          story={story}
                          open={isOpen(open, storyKey)}
                          onToggle={() => toggle(storyKey)}
                          onChange={(patch) => updateStory(storyKey, patch)}
                          onRemove={() => setStories((current) => current.filter((item) => item.clientKey !== storyKey))}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          {unassignedStories.map((story, storyIndex) => {
            const storyKey = story.clientKey || `unassigned-s-${storyIndex}`;
            return (
              <StoryNode
                key={storyKey}
                storyKey={storyKey}
                story={story}
                open={isOpen(open, storyKey)}
                onToggle={() => toggle(storyKey)}
                onChange={(patch) => updateStory(storyKey, patch)}
                onRemove={() => setStories((current) => current.filter((item) => item.clientKey !== storyKey))}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

const StoryNode: React.FC<{
  storyKey: string;
  story: ProjectStory;
  open: boolean;
  onToggle: () => void;
  onChange: (patch: Partial<ProjectStory>) => void;
  onRemove: () => void;
}> = ({ storyKey, story, open, onToggle, onChange, onRemove }) => (
  <div className="rounded-lg border border-gray-100 bg-gray-50">
    <TreeRow
      open={open}
      onToggle={onToggle}
      label={story.title.trim() || 'New story'}
      kind="Story"
      onRemove={onRemove}
    />
    {open && (
      <div className="border-t border-gray-100 px-3 py-3 space-y-3">
        <Input
          id={`story-title-${storyKey}`}
          label="Story title"
          value={story.title}
          onChange={(e) => onChange({ title: e.target.value })}
        />
        <label className="block text-sm font-medium text-gray-700">
          Description
          <textarea
            className="mt-1 w-full rounded-md border-gray-300"
            rows={3}
            value={story.description || ''}
            onChange={(e) => onChange({ description: e.target.value })}
          />
        </label>
        <label className="block text-sm font-medium text-gray-700">
          Acceptance criteria
          <textarea
            className="mt-1 w-full rounded-md border-gray-300"
            rows={3}
            value={story.acceptanceCriteria || ''}
            onChange={(e) => onChange({ acceptanceCriteria: e.target.value })}
          />
        </label>
      </div>
    )}
  </div>
);

const TreeRow: React.FC<{
  open: boolean;
  onToggle: () => void;
  label: string;
  kind: string;
  onAdd?: () => void;
  addLabel?: string;
  onRemove?: () => void;
}> = ({ open, onToggle, label, kind, onAdd, addLabel, onRemove }) => (
  <div className="flex items-center gap-1 px-2 py-1.5">
    <button
      type="button"
      className="shrink-0 rounded p-1 text-gray-600 hover:bg-gray-100"
      aria-expanded={open}
      aria-label={`${open ? 'Collapse' : 'Expand'} ${kind} ${label}`}
      onClick={onToggle}
    >
      {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
    </button>
    <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600">{kind}</span>
    <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900">{label}</span>
    {onAdd && (
      <button
        type="button"
        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
        aria-label={addLabel}
        title={addLabel}
        onClick={(e) => { e.stopPropagation(); onAdd(); }}
      >
        <Plus className="h-4 w-4" />
      </button>
    )}
    {onRemove && (
      <button type="button" className="shrink-0 px-2 text-xs text-error-600 hover:underline" onClick={(e) => { e.stopPropagation(); onRemove(); }}>
        Remove
      </button>
    )}
  </div>
);

export default ProjectRequirementsTree;
